/**
 * Utility functions for generating item codes and barcodes
 */

/**
 * Generates an item code and barcode based on an item name
 * @param itemName - The name of the item
 * @returns An object containing the generated itemCode and barcode
 */
export const generateItemCode = (itemName: string = ""): { itemCode: string; barcode: string } => {
    let code: string;

    if (itemName) {
        // Clean the name - remove special characters and keep only alphanumeric
        const cleanName = itemName.replace(/[^a-zA-Z0-9\s]/g, '').trim().toUpperCase();
        const nameParts = cleanName.split(/\s+/);

        if (nameParts.length > 1) {
            // For multi-word names, use first 3 chars of first word + first letter of each other word
            code = nameParts[0].substring(0, 3);
            for (let i = 1; i < nameParts.length && i < 4; i++) {
                code += nameParts[i].charAt(0);
            }
        } else if (cleanName.length >= 4) {
            // For single word, use first 4 chars
            code = cleanName.substring(0, 4);
        } else {
            // For very short names, use what we have and pad
            code = cleanName;
        }

        // Add a 2-digit hash based on the full name (simple sum of char codes)
        let nameHash = 0;
        for (let i = 0; i < itemName.length; i++) {
            nameHash = (nameHash + itemName.charCodeAt(i)) % 100;
        }

        // Add date component (YYMM)
        const date = new Date();
        const year = date.getFullYear().toString().slice(2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');

        // Combine everything: code + hash + date component
        code = (code + nameHash.toString().padStart(2, '0') + year + month).substring(0, 10);
    } else {
        // Fallback for empty name
        const date = new Date();
        const year = date.getFullYear().toString().slice(2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        code = `ITEM${year}${month}${day}`;
    }

    // Ensure code is exactly 10 chars, pad if needed
    code = code.padEnd(10, '0').substring(0, 10);

    // Generate barcode based on the code
    const barcode = generateBarcode(code);

    return { itemCode: code, barcode };
};

/**
 * Generates a barcode with checksum based on an item code
 * @param itemCode - The item code to base the barcode on
 * @returns The generated barcode string
 */
export const generateBarcode = (itemCode: string): string => {
    // Clean the code - keep only alphanumeric
    const cleanCode = itemCode.replace(/[^A-Z0-9]/g, '');

    // Create a consistent prefix for identifying product barcodes
    const prefix = "27"; // Example prefix for internal products

    // Combine prefix with a portion of the item code
    const baseCode = prefix + cleanCode;

    // Ensure we have exactly 12 digits for EAN-13 (13th will be checksum)
    const paddedCode = baseCode.padEnd(12, '0').substring(0, 12);

    // Calculate EAN-13 checksum
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        const digit = parseInt(paddedCode.charAt(i), 10) || 0;
        sum += i % 2 === 0 ? digit : digit * 3;
    }
    const checksum = (10 - (sum % 10)) % 10;

    return paddedCode + checksum;
}; 